import classNames from 'classnames';
import FormInput from 'components/Input/FormInput';
import Text from 'components/Text/Text';
import React, { useCallback } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './DraggableModifierGroup.module.scss';

interface IDraggableModifierGroup {
	modifierGroupId: string;
	draggableInnerRef: ((element?: HTMLElement | null | undefined) => any) | undefined;
	provided: DraggableProvided;
	positionNumber: number;
	changeUpperLimit: (id: string, value: string) => void;
	changeLowerLimit: (id: string, value: string) => void;
	toggleLimitsProtectedFromSync: (id: string, value: boolean) => void;
	label: string;
	onDelete: () => void;
	isLimitsProtectedFromSync: boolean;
	hasDragAccess?: boolean;
}

const DraggableModifierGroup: React.FC<IDraggableModifierGroup> = ({
	modifierGroupId,
	draggableInnerRef,
	provided,
	positionNumber,
	label,
	changeUpperLimit,
	changeLowerLimit,
	toggleLimitsProtectedFromSync,
	onDelete,
	isLimitsProtectedFromSync,
	hasDragAccess = true,
}) => {
	const dragMarkClasses = classNames(style.dragMark, { [style.hidden]: !hasDragAccess });

	const handleChangeUpperLimit = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const onlyDigits = e.target.value.replace(/\D/g, '');
			changeUpperLimit(modifierGroupId, onlyDigits);
			return onlyDigits;
		},
		[changeUpperLimit],
	);

	const handleChangeLowerLimit = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const onlyDigits = e.target.value.replace(/\D/g, '');
			changeLowerLimit(modifierGroupId, onlyDigits);
			return onlyDigits;
		},
		[changeLowerLimit],
	);

	const handleToggleLimitsProtectedFromSync = useCallback(
		(e: CheckboxChangeEvent) => {
			toggleLimitsProtectedFromSync(modifierGroupId, e.target.checked);
		},
		[toggleLimitsProtectedFromSync],
	);

	return (
		<div
			className={style.container}
			ref={hasDragAccess ? draggableInnerRef : undefined}
			{...provided.draggableProps}>
			<div className={dragMarkClasses} {...provided.dragHandleProps}>
				<svg>
					<use xlinkHref={`${breadcrumbs}#dragMark`} />
				</svg>
			</div>
			<div className={style.content}>
				<div className={style.labelRow}>
					<Text
						className={style.positionNumber}
						translationNameSpace="form"
						translationKey="positionNumberLabel"
						translationOptions={{ positionNumber }}
						theme="secondary"
						size="s"
						weight="bold"
					/>
					<Text
						className={style.labelText}
						translationKey={label}
						theme="secondary"
						size="xs"
						withTooltip={true}
					/>
					<div className={style.deleteBtn} onClick={onDelete}>
						<svg className={style.deleteIcon}>
							<use xlinkHref={`${breadcrumbs}#closeSelectItem`} />
						</svg>
					</div>
				</div>
				<div className={style.limits}>
					<FormInput
						testId="UpperLimitField"
						name={`upperLimit-${modifierGroupId}`}
						label="upperLimitLabel"
						getValueFromEvent={handleChangeUpperLimit}
					/>
					<FormInput
						testId="LowerLimitField"
						name={`lowerLimit-${modifierGroupId}`}
						label="lowerLimitLabel"
						getValueFromEvent={handleChangeLowerLimit}
					/>
				</div>
				<Checkbox
					data-test="IsLimitsProtectedFromSyncCheckbox"
					name={`isLimitsProtectedFromSync-${modifierGroupId}`}
					className={style.checkbox}
					onChange={handleToggleLimitsProtectedFromSync}
					checked={isLimitsProtectedFromSync}>
					<Text translationNameSpace="form" translationKey="protectFromSyncLabel" asFragment={true} />
				</Checkbox>
			</div>
		</div>
	);
};

export default React.memo(DraggableModifierGroup);
